import Attachments from '@/components/Attachments';
import ChartPlayer from '@/components/ChartPlayer';
import DocumentPlayer from '@/components/DocumentPlayer';
import GoBackMenu from '@/components/GoBackNavbar';
import LMSAudioAndVideoPlayer from '@/components/LMSAudioAndVideoPlayer';
import LMSHtmlPlayer from '@/components/LMSHtmlPlayer';
import Loader from '@/components/Loader';
import ReadMore from '@/components/ReadMore';
import ScormLink from '@/components/ScormLink';
import Constants from '@/Constants';
import { useCourseStudentView } from '@/hooks/useCourseStudentView';
import { AvailableTranslation } from '@/models/AvailableTranslation';
import { ContentMetadata } from '@/models/ContentMetadata';
import { CurrentLearningMapRequest } from '@/models/CurrentLearningMapRequest';
import { LanguageType } from '@/models/LanguageType';
import { LessonEnrollment } from '@/models/LessonEnrollment';
import { LessonRequestData } from '@/models/LessonRequestData';
import { Status } from '@/models/Status';
import { analyticsService, LMSEvents } from '@/services/AnalyticsService';
import BotService from '@/services/BotService';
import { contentService } from '@/services/ContentService';
import { courseService } from '@/services/CourseService';
import { ApplicationState } from '@/store';
import { clearContent } from '@/store/item/action';
import { getCurrentLearningMapRequest } from '@/store/learningMap/action';
import { coursesHavingAContentClear, finishLessonRequest, startLessonRequest } from '@/store/list/action';
import { clearCurrentContentLanguage } from '@/store/locale/action';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import ExternalPlayer from '../../ExternalPlayer';
import './style.scss';

const ContentScreen: React.FC = () => {
    const dispatch = useDispatch();

    const { courseId: courseIdString, lessonId: lessonIdString, enrollmentId: enrollmentIdString } = useParams<{
        courseId: string;
        lessonId: string;
        enrollmentId: string;
    }>();

    const courseId: number = Number(courseIdString);
    const lessonId: number = Number(lessonIdString);
    const classEnrollmentId: number = Number(enrollmentIdString);

    const { content, personId, currentContentLanguage, strings, learningMap } = useSelector((state: ApplicationState) => {
        return {
            content: state.itens.currentData.content,
            personId: state.identity.data.person.id,
            courseEnrollment: state.lists.currentData.courseEnrollment,
            courseName: state.lists.currentData.course && state.lists.currentData.course.name,
            currentContentLanguage: state.locale.currentContentLanguage || (state.locale.data._LANGUAGE as LanguageType),
            strings: state.locale.data,
            learningMap: state.learningMap.data.currentLearningMap,
        };
    });
    const [metaData, setMetaData] = useState<ContentMetadata>({} as ContentMetadata);
    const [contentTranslation, setContentTranslation] = useState<AvailableTranslation>();
    const [mustShowDescription, setMustShowDescription] = useState<boolean>(true);
    const [lessonStatus, setLessonStatus] = useState<string>('');
    const [lessonFinished, setLessonFinished] = useState<boolean>(false);

    const { courseStudentView, course, programClass, courseEnrollment, mutate, getLessonEnrollment } = useCourseStudentView({
        moduleId: Constants.LMS_MODULE_ID,
        courseId,
        classEnrollmentId,
    });

    const lessonEnrollment: LessonEnrollment = getLessonEnrollment(lessonId);

    const courseName: string = course.name;

    const clearAllOnUnmount = () => {
        return () => {
            dispatch(clearContent());
            dispatch(clearCurrentContentLanguage());
            dispatch(coursesHavingAContentClear());
        };
    };

    const getCurrentLearningMap = (): void => {
        dispatch(getCurrentLearningMapRequest({} as CurrentLearningMapRequest));
    };

    const loadContentTranslationAndMetadata = (): void => {
        if (content) {
            setMetaData(contentService.getContentMetadata(content));

            updateContentTranslation();

            const { mediaType, description } = content;

            if (mediaType === 'HTML' || mediaType === 'CHART' || !description) {
                setMustShowDescription(false);
            }
        }
    };

    const updateContentTranslation = () => {
        setContentTranslation(contentService.getContentTranslationByLanguageType(content, currentContentLanguage));
    };

    const updateLessonStatus = (): void => {
        if (lessonEnrollment) {
            const { COMPLETED, PASSED, FAILED } = Constants.lessonEnrollmentStatus;
            const completedStatusList = [COMPLETED, PASSED, FAILED];
            setLessonFinished(completedStatusList.indexOf(lessonEnrollment.status) !== -1);

            const status = courseService.getLessonStatusLabel(lessonEnrollment, strings);
            setLessonStatus(status);
        }
    };

    const authorNameValidation = () => (!content.author ? strings.DEFAULT_AUTHOR : content.author);

    const finishLesson = (): void => {
        dispatch(
            finishLessonRequest({
                lessonId,
                personId,
                courseId,
                moduleId: Constants.LMS_MODULE_ID,
                lessonStartAndFinishData: { classEnrollmentId },
                showLoader: false,
            } as LessonRequestData),
        );

        updateLocalStatus('COMPLETED');

        setLessonFinished(true);
        onFinish();
    };

    const startLesson = useCallback((): void => {
        dispatch(
            startLessonRequest({
                lessonId,
                courseId,
                personId,
                lessonStartAndFinishData: { classEnrollmentId },
            } as LessonRequestData),
        );

        updateLocalStatus('INCOMPLETE');
    }, [lessonId, courseId, personId, classEnrollmentId, courseEnrollment]);

    const updateLocalStatus = (status: Status): void => {
        const { lessonEnrollments = [] } = courseEnrollment;
        const index = lessonEnrollments.findIndex(({ lesson }) => lesson.id === lessonId);

        if (index !== -1 && lessonEnrollments[index].status !== 'COMPLETED') {
            lessonEnrollments[index].status = status;
            mutate({ ...courseStudentView, courseEnrollment }, false);
        }
    };

    const startContent = (): void => {
        if (lessonEnrollment && content) {
            analyticsService.logEvent(LMSEvents.ACCESS_LESSON, {
                entityId: lessonId,
                entityName: content.name,
                entityType: Constants.entityTypes.LESSON,
            });

            analyticsService.logEvent(LMSEvents.CONSUMPTION_COURSE, {
                entityId: courseId,
                entityName: courseName,
                entityType: Constants.entityTypes.COURSE,
            });
        }
    };

    const onFinish = (): void => {
        if (lessonStatus !== strings.CONCLUDED) {
            setLessonStatus(strings.CONCLUDED);
            sendNotification();
        }
    };

    const finishScormLesson = (): void => {
        sendNotification();
    };

    const getPlayerByType = (): React.ReactNode => {
        switch (content.mediaType) {
            case 'AUDIO':
            case 'VIDEO':
                return (
                    <LMSAudioAndVideoPlayer
                        content={content}
                        finishLesson={finishLesson}
                        isLessonFinished={lessonFinished}
                        startVideoLesson={startLesson}
                    />
                );
            case 'HTML':
                return <LMSHtmlPlayer content={content} finishLesson={finishLesson} startLesson={startLesson} />;
            case 'EXTERNAL':
                return (
                    <ExternalPlayer
                        content={content}
                        finishLesson={finishLesson}
                        lessonStatus={lessonStatus}
                        metadata={metaData}
                        startLesson={startLesson}
                    />
                );

            case 'DOCUMENT':
                return <DocumentPlayer content={content} finishLesson={finishLesson} startLesson={startLesson} />;
            case 'CHART':
                return <ChartPlayer content={content} finishLesson={finishLesson} startLesson={startLesson} />;
            default:
                if (content.type === 'ScormContent') {
                    return <ScormLink content={content} onFinish={finishScormLesson} />;
                }
                return null;
        }
    };

    const lessonDescriptionClass = () =>
        content.mediaType === 'EXTERNAL' || content.type === 'ScormContent'
            ? 'lesson-content__description_with_shadow'
            : 'lesson-content__description';

    const sendNotification = (): void => BotService.sendConsumedLearningMapNotification(dispatch, learningMap, Number(content.workload));

    useEffect(clearAllOnUnmount, []);

    useEffect(getCurrentLearningMap, []);

    useEffect(loadContentTranslationAndMetadata, [content]);

    useEffect(updateLessonStatus, [lessonEnrollment.status]);

    useEffect(updateContentTranslation, [currentContentLanguage]);

    useEffect(startContent, [content, lessonEnrollment]);

    if (!content || !contentTranslation || !programClass.id) {
        return <Loader styles={{ height: 'auto' }} />;
    }

    return (
        <>
            <div className="content-page">
                <GoBackMenu />
                <div className="content-main">
                    <div className="item-bg" id={Constants.TOP_IMG_ID}>
                        <img className="item-bg__img" src={content.mainImage} />
                    </div>
                    <div className="container">
                        {/* TODO: Aguardando deploy do release 052022-1 sem o Toast */}
                        {/* <NextLessonToast /> */}
                        <div className="row">
                            <div className="col-sm-8 offset-sm-2">
                                <div className="lesson-content">
                                    <h1 className="lesson-content__title">{contentTranslation.name}</h1>
                                    <div className="lesson-content__info">
                                        <div>
                                            {authorNameValidation()}
                                            <i className="material-icons align-middle">{Constants.icons.CIRCLE}</i>
                                            {lessonStatus}
                                        </div>
                                    </div>

                                    <div className="lesson-content__info">
                                        <div>{`${strings.CLASS_ID} #${programClass.id}`}</div>
                                    </div>
                                    <div className="lesson-content__info">
                                        <div>{`${contentTranslation.workload} ${strings.MINUTES}`}</div>
                                    </div>
                                    {(content.mediaType === 'HTML' || content.mediaType === 'CHART') && contentTranslation.description && (
                                        <div className="lesson-content__html-description">{contentTranslation.description}</div>
                                    )}

                                    <div className="lesson-content__stage">{getPlayerByType()}</div>

                                    {mustShowDescription && (
                                        <div className={lessonDescriptionClass()}>
                                            <ReadMore maxHeight="120" primaryColor value={contentTranslation.description} />
                                        </div>
                                    )}
                                    <Attachments content={content} locale={content?.locale} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};
export default ContentScreen;
